import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../environments/test.environment';
import { Observable } from 'rxjs';
import { UserRole } from './user-role.service';

export interface User {
  firstname: string;
  lastname: string;
  email: string;
  phoneNumber: string;
  job_title: string;
  id: number;
  username: string;
  roles: string[];
  profilePhoto?: string;
}

export interface NewUser {
  id?: number | null;
  username: string | null;
  password: string | null;
  company?: string | null;
  company_id?: string | null;
  firstname?: string | null;
  lastname?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  job_title?: string | null;
  roles: string[] | UserRole[];
}

export interface Role {
  id: number;
  name: string;
  description: string[];
}

export interface UserRoleDto {
  userId: number;
  roleId: string;
}

@Injectable({ providedIn: 'root' })
export class UserService {
  constructor(private http: HttpClient) {}

  downloadInstaller(): Observable<Blob> {
    return this.http.get(`${environment.apiBaseUrl}/api/updates/download`, { responseType: 'blob' });
  }

  getAllUsers(companyId?: string | null): Observable<User[]> {
    const url = companyId
      ? `${environment.apiBaseUrl}/api/users/all?companyId=${companyId}`
      : `${environment.apiBaseUrl}/api/users/all`;
    return this.http.get<User[]>(url);
  }

  createUser(user: NewUser): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/api/users/create`, user);
  }

  getAllRoles(): Observable<Role[]> {
    return this.http.get<Role[]>(`${environment.apiBaseUrl}/api/roles/all`)
  }
  
  assignRoleToUser(dto: UserRoleDto): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/api/roles/assign`, dto, { responseType: 'text' as 'json' });
  }

  removeRoleFromUser(dto: UserRoleDto): Observable<any> {
    return this.http.request('delete', `${environment.apiBaseUrl}/api/roles/remove`, { body: dto, responseType: 'text' as 'json' });
  }

  getUserById(id: string): Observable<User> {
    return this.http.get<User>(`${environment.apiBaseUrl}/api/users/${id}`);
  }

  uploadProfilePhoto(userId: string, formData: FormData) {
  return this.http.post<{ url: string }>(`${environment.apiBaseUrl}/api/users/${userId}/upload-photo`, formData);
}

  updateUserContact(userId: string, data: { firstname?: string; lastname?: string; email?: string; phoneNumber?: string, jobTitle?: string }) {
  return this.http.post(`${environment.apiBaseUrl}/api/users/${userId}/update`, data);
}

  getUsernameById(userId: string): Observable<string> {
    return this.http.get(`${environment.apiBaseUrl}/api/users/${userId}/username`, {
      responseType: 'text'
    });
  }

  deactivateUser(userId: number): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/api/users/${userId}/deactivate`, { responseType: 'text' as 'json' });
  }

  updateUserInfo(user: User, userId: number): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/api/users/${userId}/update/info`, user);
  }

  resetUserPassword(userId: number, newPassword: string): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/api/users/${userId}/reset/password`, { newPassword }, { responseType: 'text' as 'json' });
  }

  updateUserPassword(userId: string | null, NewPassword: string, OldPassword: string): Observable<any> {
    return this.http.post(`${environment.apiBaseUrl}/api/users/${userId}/change/password`, { NewPassword, OldPassword }, { responseType: 'text' as 'json' });
  }

  checkUsernameHasEmail(username: string): Observable<{ hasEmail: boolean }> {
    return this.http.get<{ hasEmail: boolean }>(`${environment.apiBaseUrl}/api/users/${username}/has-email`);
  }

  getMaskedEmail(username: string): Observable<{ maskedEmail: string }> {
    return this.http.get<{ maskedEmail: string }>(`${environment.apiBaseUrl}/api/users/${username}/masked-email`);
  }

  sendPasswordResetEmail(username: string): Observable<{ success: boolean; message: string; maskedEmail?: string }> {
    return this.http.post<{ success: boolean; message: string; maskedEmail?: string }>(`${environment.apiBaseUrl}/api/users/forgot-password`, { username });
  }

  validateResetToken(token: string): Observable<{ valid: boolean; message?: string }> {
    const encoded = encodeURIComponent(token || '');
    return this.http.get<{ valid: boolean; message?: string }>(`${environment.apiBaseUrl}/api/users/validate-reset-token?token=${encoded}`);
  }

  resetPasswordWithToken(token: string, newPassword: string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${environment.apiBaseUrl}/api/users/reset-password`, { token, newPassword });
  }
}
